/**
 * The centerpiece: every learner (rows) on every concept (columns, grouped by
 * topic). Cell colour = mastery state; a dot marks a flagged misconception.
 * Click a row to open the learner; click a topic header to open item analysis.
 */

import { AlertTriangle } from 'lucide-react';
import { cn } from '../../../lib/utils';
import type { ClassRoster, Learner } from './mockData';
import { conceptMasteryOf, misconceptionLabel } from './mockData';
import { MASTERY_META, MasteryLegend } from './charts';

export function MasteryHeatmap({ roster, onSelectLearner, onSelectTopic }: {
  roster: ClassRoster;
  onSelectLearner: (l: Learner) => void;
  onSelectTopic: (topicId: string) => void;
}) {
  return (
    <div className="rounded-2xl border border-black/10 bg-white p-6 dark:border-white/10 dark:bg-white/[0.04]">
      <div className="mb-1 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="font-serif text-[16px] font-semibold tracking-tight">Mastery map</h3>
          <p className="mt-0.5 text-[12.5px] text-[#8A8593]">Every learner on every concept. A dot marks a flagged misconception. Select a learner for the full picture.</p>
        </div>
        <MasteryLegend />
      </div>

      <div className="-mx-2 mt-4 overflow-x-auto px-2">
        <table className="border-separate" style={{ borderSpacing: 0 }}>
          <thead>
            <tr>
              <th rowSpan={2} className="sticky left-0 z-20 bg-white pr-3 text-left align-bottom dark:bg-[#14141d]" />
              {roster.topics.map((t) => (
                <th key={t.id} colSpan={t.conceptTags.length} className="px-1 pb-2 align-bottom">
                  <button onClick={() => onSelectTopic(t.id)}
                    className="w-full truncate rounded-md px-2 py-1 text-[11.5px] font-bold uppercase tracking-wide text-[#56505F] transition-colors hover:bg-black/[0.04] dark:text-[#B2B0C0] dark:hover:bg-white/[0.06]">
                    {t.title}
                  </button>
                </th>
              ))}
            </tr>
            <tr>
              {roster.concepts.map((c) => (
                <th key={c.tag} className="p-0 align-bottom">
                  <div className="relative h-[118px] w-9">
                    <span className="absolute bottom-1.5 left-1/2 origin-bottom-left whitespace-nowrap text-[11px] font-medium text-[#56505F] dark:text-[#B2B0C0]"
                      style={{ transform: 'translateX(-1px) rotate(-52deg)' }}>{c.label}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {roster.learners.map((l) => (
              <tr key={l.id} className="group">
                <th className="sticky left-0 z-10 bg-white py-[3px] pr-3 text-left dark:bg-[#14141d]">
                  <button onClick={() => onSelectLearner(l)}
                    className="flex w-full items-center gap-2 rounded-lg px-2 py-1 text-left transition-colors hover:bg-black/[0.04] dark:hover:bg-white/[0.06]">
                    <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[#7C3AED]/12 text-[10px] font-bold text-[#6D28D9] dark:text-[#B69BF5]">{l.rollNo}</span>
                    <span className="min-w-[84px] flex-1 truncate text-[13px] font-medium">{l.firstName}</span>
                    <span className="w-8 text-right text-[11.5px] font-semibold tabular-nums text-[#8A8593]">{l.overallMasteryPercent}%</span>
                  </button>
                </th>
                {roster.concepts.map((c) => {
                  const m = conceptMasteryOf(l, c.tag);
                  const state = m?.state ?? 'not-attempted';
                  const meta = MASTERY_META[state];
                  const flagged = !!m?.lastMisconceptionTag;
                  const title = `${l.firstName} · ${c.label}: ${meta.label}${m && m.total ? ` (${m.correct}/${m.total})` : ''}${flagged ? `\nMisconception: ${misconceptionLabel(m!.lastMisconceptionTag!)}` : ''}`;
                  return (
                    <td key={c.tag} className="p-[2px]">
                      <button onClick={() => onSelectLearner(l)} title={title}
                        className="relative grid h-8 w-8 place-items-center rounded-[5px] transition-transform hover:scale-110 hover:ring-2 hover:ring-[#6D28D9]/40"
                        style={{ background: meta.color, opacity: state === 'not-attempted' ? 0.4 : 1 }}>
                        {flagged && <AlertTriangle className="h-3 w-3 text-white" strokeWidth={2.5} />}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className={cn('mt-3 text-[11.5px] text-[#8A8593]')}>
        {roster.learners.length} learners · {roster.concepts.length} concepts · tap any cell or name to drill in.
      </p>
    </div>
  );
}
